// Source-tenant device search API surface (FD-112). Consumes the manager read
// GET /v1/migrations/{id}/source-devices — the contract row in docs/TASKS.md
// (FD-112 v1.0) is the authoritative shape; the mirrored interfaces live in
// src/types/sourceDevices.ts.
//
// This is a discovery/validate layer in front of the UNCHANGED add-devices
// flow: the search returns real source-tenant managed devices for an exact
// serial, the admin confirms/selects, and the selected serial numbers feed the
// existing serial-keyed POST /v1/migrations/{id}/devices. This module adds no
// write path.

import { ApiError, getJson } from './client';
import { ApiEnvelope, isApiEnvelope } from './migrations';
import { SourceDevicesResponse } from '../types/sourceDevices';

// The consent-class 409 (FD-096 shape) and the whole-request 502 GRAPH_ERROR
// are byte-identical to the identity endpoints, so their recognisers are reused
// verbatim — one renderer (ConsentMessageBar) and one transient-error path
// across every Graph-backed read.
export { extractConsentError, isGraphError } from './identity';

// Server contract (FD-112 v1.0): `search` is required, trimmed, min 3 chars
// (large-tenant guard, mirrors tenant-users). Callers gate on this before
// fetching. Exact-match only (`managedDevice.serialNumber` supports `eq`/`or`).
export const MIN_SEARCH_LENGTH = 3;

export interface SearchSourceDevicesParams {
  search: string;
  top?: number;
  skipToken?: string | null;
}

export async function searchSourceDevices(
  migrationId: string,
  params: SearchSourceDevicesParams,
): Promise<SourceDevicesResponse> {
  const search = new URLSearchParams();
  search.set('search', params.search);
  if (params.top) search.set('top', String(params.top));
  if (params.skipToken) search.set('skipToken', params.skipToken);
  const path =
    `/v1/migrations/${encodeURIComponent(migrationId)}/source-devices?${search.toString()}`;
  const envelope = await getJson<ApiEnvelope<SourceDevicesResponse>>(path);
  return envelope.data;
}

// The non-consent machine codes this endpoint returns (FD-112 v1.0):
//   400 D2C_HAS_NO_SOURCE_TENANT — D2C migration has no source tenant (should
//       not occur — the UI only shows lookup for T2T — but handled defensively)
//   400 (other)                 — bad params (missing/short search, non-int top)
//   404                         — unknown migration
//   502 GRAPH_ERROR             — token-acquisition or Graph failure after retry
// Consent-class 409s are recognised separately via extractConsentError (reused
// from identity.ts) so they render the ConsentMessageBar, not a raw error.
export type SourceDeviceSearchErrorCode =
  | 'D2C_HAS_NO_SOURCE_TENANT'
  | 'INVALID_PARAMS'
  | 'MIGRATION_NOT_FOUND'
  | 'GRAPH_ERROR'
  | 'UNKNOWN';

export function sourceDeviceSearchErrorCode(err: unknown): SourceDeviceSearchErrorCode {
  if (!(err instanceof ApiError)) return 'UNKNOWN';

  const envelopeCode = readEnvelopeErrorCode(err.body);
  if (envelopeCode === 'D2C_HAS_NO_SOURCE_TENANT') return 'D2C_HAS_NO_SOURCE_TENANT';
  if (envelopeCode === 'GRAPH_ERROR') return 'GRAPH_ERROR';

  switch (err.status) {
    case 400:
      return 'INVALID_PARAMS';
    case 404:
      return 'MIGRATION_NOT_FOUND';
    case 502:
      return 'GRAPH_ERROR';
    default:
      return 'UNKNOWN';
  }
}

function readEnvelopeErrorCode(body: unknown): string | undefined {
  if (!isApiEnvelope(body)) return undefined;
  const code = (body as ApiEnvelope<unknown>).error?.code;
  return typeof code === 'string' ? code : undefined;
}
